import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { FC, useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  DesiredDateInterval,
  DesiredDateType
} from '@/api/models/requests/lib/requests.types'
import { ServerValidationError } from '@/api/shared/types'
import { TIME_SLOTS } from '@/app/constants/constants'
import { useServerTimeStore } from '@/components/entities/common/model/server-time'
import {
  DEFAULT_DATE_ONLY_FORMAT,
  DEFAULT_TIMEZONE
} from '@/components/entities/requests/model/constants'
import { WeekDay } from '@/components/shared/datepicker/model/types'
import { DatePickerWeek } from '@/components/shared/datepicker/ui/DatePickerWeek'
import AppInputMessage from '@/components/shared/input/ui/AppInputMessage'

type FormValues = {
  date: string
  timeSlot?: string | null
  desiredDateType: DesiredDateType
  desiredDateInterval?: DesiredDateInterval
}

type DatePickerValue = {
  date: string
  timeSlot?: string | null
  type: DesiredDateType
}

type Props = {
  requestError?: AxiosError<ServerValidationError> | null
}

const RequestDesiredExecutionTimeFormBlock: FC<Props> = ({ requestError }) => {
  const [isDirty, setIsDirty] = useState(false)
  const {
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors }
  } = useFormContext<FormValues>()

  const serverOffsetMs = useServerTimeStore(state => state.serverOffsetMs)

  const mskNow = () => dayjs(Date.now() + serverOffsetMs).tz(DEFAULT_TIMEZONE)

  const applyDesiredDateServerError = useCallback(() => {
    const serverErrors = requestError?.response?.data?.errors
    if (!serverErrors?.length) {
      return
    }

    const desiredDateError = serverErrors.find(error =>
      error.field.includes('desiredDateInterval')
    )

    if (desiredDateError?.message) {
      setError('date', {
        type: 'server',
        message: desiredDateError.message
      })
    }
  }, [requestError, setError])

  useEffect(() => {
    applyDesiredDateServerError()
  }, [applyDesiredDateServerError])

  const currentWeek = (): WeekDay[] => {
    const today = mskNow()
    const todayKey = today.format(DEFAULT_DATE_ONLY_FORMAT)
    const week = []

    for (let i = 0; i < 7; i++) {
      const day = today.add(i, 'day')
      const dayKey = day.format(DEFAULT_DATE_ONLY_FORMAT)

      week.push({
        date: dayjs(dayKey).toDate(),
        displayDate: day.format('DD'),
        dayOfWeek: day.format('dd'),
        fullDate: dayKey,
        isToday: dayKey === todayKey,
        isDisabled: dayKey < todayKey
      })
    }
    return week
  }

  const currentMonth = () => {
    const month = mskNow().format('MMMM')
    return month.charAt(0).toUpperCase() + month.slice(1)
  }

  const handleChangeDate = (value: DatePickerValue) => {
    clearErrors('date')

    if (!isDirty) {
      setIsDirty(true)
    }

    const { date, timeSlot, type } = value

    if (date) {
      const selectedDay = dayjs(date).format(DEFAULT_DATE_ONLY_FORMAT)
      const todayKey = mskNow().format(DEFAULT_DATE_ONLY_FORMAT)

      if (selectedDay >= todayKey) {
        setValue('date', selectedDay, {
          shouldDirty: true
        })

        if (timeSlot) {
          setValue('timeSlot', timeSlot, {
            shouldDirty: true
          })
        } else {
          setValue('timeSlot', null, { shouldDirty: true })
        }
      }
    } else {
      setValue('date', '', { shouldDirty: true })
      setValue('timeSlot', null, { shouldDirty: true })
    }

    switch (type) {
      case DesiredDateType.ANY: {
        setValue('desiredDateType', DesiredDateType.ANY, { shouldDirty: true })
        setValue('desiredDateInterval', undefined, { shouldDirty: true })
        break
      }
      case DesiredDateType.ASAP: {
        setValue('desiredDateType', DesiredDateType.ASAP, { shouldDirty: true })
        setValue('desiredDateInterval', undefined, { shouldDirty: true })
        break
      }
      case DesiredDateType.INTERVAL: {
        if (date && timeSlot) {
          setValue('desiredDateType', DesiredDateType.INTERVAL, {
            shouldDirty: true
          })

          const selectedDate = dayjs(date)
          const selectedTimeSlot = TIME_SLOTS.find(
            slot => slot.displayText === timeSlot
          )

          const startDateTime = selectedDate.format(
            `YYYY-MM-DDT${selectedTimeSlot?.startTime}:00`
          )
          const endDateTime = selectedDate.format(
            `YYYY-MM-DDT${selectedTimeSlot?.endTime}:00`
          )

          setValue(
            'desiredDateInterval',
            {
              start: startDateTime,
              end: endDateTime
            },
            { shouldDirty: true }
          )
        } else {
          setValue('desiredDateType', DesiredDateType.NONE, {
            shouldDirty: true
          })
          setValue('desiredDateInterval', undefined, { shouldDirty: true })
        }
        break
      }
      default: {
        setValue('desiredDateType', DesiredDateType.NONE, { shouldDirty: true })
        setValue('desiredDateInterval', undefined, { shouldDirty: true })
      }
    }
  }

  const currentDate = watch('date')
  const currentTimeSlot = watch('timeSlot')
  const desiredDateType = watch('desiredDateType')

  const getPickerType = (): DesiredDateType | null => {
    if (desiredDateType === DesiredDateType.ANY) {
      return DesiredDateType.ANY
    }
    if (desiredDateType === DesiredDateType.ASAP) {
      return DesiredDateType.ASAP
    }
    if (desiredDateType === DesiredDateType.INTERVAL && currentDate) {
      return DesiredDateType.INTERVAL
    }
    return null
  }

  return (
    <div className="p-32 pt-0 lg:p-16 sm:px-0">
      <DatePickerWeek
        timeSlots={TIME_SLOTS}
        label="Желаемое время выполнения"
        date={currentDate || undefined}
        timeSlot={currentTimeSlot || null}
        currentMonth={currentMonth()}
        currentWeek={currentWeek()}
        selectedType={getPickerType()}
        onChange={handleChangeDate}
        isDirty={isDirty}
      />
      <AppInputMessage
        message={errors.date?.message}
        className="items-start"
        iconClassName="text-red-70 flex-shrink-0 pt-2"
      />
    </div>
  )
}

export { RequestDesiredExecutionTimeFormBlock }
